import { CalendarCheck, Car, ConciergeBell, Headset, Target } from '@lucide/vue'
import { SECTION_BY_NAME, SECTIONS } from '@/router/sections'
import { can, isOrgAdmin, primaryRole, viewerLevel } from '@/services/rbac'
import { WORKSPACE_BY_ROLE } from '@/workspaces/registry'
import type { SectionDef, SectionNav } from '@/router/sections'
import type { CapabilityVerb, Role, ViewerCapabilities } from '@/services/rbac'
import type { WorkspaceDef } from '@/workspaces/registry'
import type { Component } from 'vue'

// Навигация каркаса — проекция реестра разделов (router/sections.ts): сайдбар
// (desktop + mobile sheet) и нижний таб-бар рендерятся из NAV_GROUPS/NAV_ITEMS,
// без правок AppSidebar / AppTabBar. Новый раздел = запись в SECTIONS, здесь
// править нечего. Мёртвых пунктов не держим: пункт живёт вместе со своим роутом.
export interface NavItem {
  /** route name из router/index.ts — по нему считается активность */
  name: string
  /** путь для RouterLink */
  to: string
  icon: Component
  /** i18n-ключ подписи (ru.json + en.json) */
  labelKey: string
  /** i18n-ключ короткой подписи под лейблом — только в поповере «Ещё» (§6-BIS.1);
      разделы таб-бара её не показывают */
  descriptionKey?: string
  /** право доступа к разделу (то же поле, что роут-гард meta.capability): нет права —
      нет и пункта. Имперсонация прозрачна — считается от позиций таргета (E18, §5) */
  capability?: CapabilityVerb
  /** роли, которым пункт не показывается, ХОТЯ право на раздел у них есть
      (ресепшн и лента записей): видимость меню ≠ право доступа */
  hiddenFor?: readonly Role[]
  /** false — пункт не показывается в нижнем таб-баре (mobile): место ограничено,
      «Пользователи» и «Журнал» живут в поповере «Ещё» */
  tabBar?: boolean
  /** Источник счётчика-бейджа на пункте навигации; резолвится в компоненте
      (AppSidebar/AppTabBar) из соответствующего стора. Сейчас единственный —
      'requestsQueue' (размер очереди свободных заявок отдела). */
  badge?: 'requestsQueue'
}

/**
 * Группа пунктов сайдбара. Сначала бизнес-блок (работа с холдингом), затем
 * сервисный (пользователи + журнал аудита) отдельной секцией. Сервисный блок
 * целиком исчезает, если ни один его пункт недоступен по роли (visibleNavGroups).
 */
export interface NavGroup {
  /** i18n-ключ заголовка группы (ru.json + en.json) */
  labelKey: string
  items: NavItem[]
}

// Заголовки блоков сайдбара; порядок блоков = порядок этого массива, порядок
// пунктов внутри блока — порядок SECTIONS.
const NAV_GROUP_LABELS: { group: NonNullable<SectionNav['group']>; labelKey: string }[] = [
  { group: 'main', labelKey: 'nav.section_label' },
  { group: 'service', labelKey: 'nav.section_service' },
]

function navItemFor(section: SectionDef, nav: SectionNav): NavItem {
  return {
    name: section.name,
    to: section.path,
    icon: section.icon,
    labelKey: section.titleKey,
    descriptionKey: nav.descriptionKey,
    capability: section.capability,
    hiddenFor: nav.hiddenFor,
    tabBar: nav.tabBar,
    badge: nav.badge,
  }
}

export const NAV_GROUPS: NavGroup[] = NAV_GROUP_LABELS.map(({ group, labelKey }) => ({
  labelKey,
  items: SECTIONS.flatMap((section) =>
    section.nav === false || (section.nav.group ?? 'main') !== group
      ? []
      : [navItemFor(section, section.nav)],
  ),
}))

/** Плоский список всех пунктов — для таб-бара и подсветки активного раздела. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

// Админ смотрящего (org-admin; служебный superuser приходит с сервера с тем же правом):
// таб-бар разворачивается в админ-раскладку, домашняя — дашборд.
export function isAdminUser(capabilities: ViewerCapabilities): boolean {
  return isOrgAdmin(capabilities)
}

// Порядок админ-раскладки таб-бара (см. isAdminUser): управленческие разделы
// вместо доменных. Все четыре доступны админу — фильтр видимости их пропустит.
const ADMIN_TAB_BAR_ORDER = ['dashboard', 'reports', 'org-structure', 'admin-users'] as const

// Пункт скрыт, когда раздел не рабочий для СТАРШЕЙ роли смотрящего (served primary_role):
// мультипозиционный (ресепшн + МОП) пункт видит — старшая роль для него рабочая.
// Осторожно: hiddenFor у СТАРШЕЙ роли спрячет пункт и у мультипозиционного смотрящего,
// которому он нужен по младшей. Сегодня hiddenFor только у level-0 роли, так не бывает.
function hiddenForViewer(hiddenFor: readonly Role[], capabilities: ViewerCapabilities): boolean {
  const role = primaryRole(capabilities)
  return role !== null && hiddenFor.includes(role)
}

// Видимость пункта под роль (ТЗ §3.3 п.3) — два независимых слоя: право на раздел
// (capability, то же что роут-гард) и решение «этой роли пункт не нужен» (hiddenFor).
function isItemVisible(item: NavItem, capabilities: ViewerCapabilities): boolean {
  if (item.capability !== undefined && !can(capabilities, item.capability)) return false
  if (item.hiddenFor !== undefined && hiddenForViewer(item.hiddenFor, capabilities)) return false
  return true
}

/**
 * Домашний роут под роль — каждая роль попадает на свой рабочий экран (тип работы,
 * не бинарный уровень): ресепшн идентифицирует гостя → контрагенты; специалисты
 * разбирают очередь отдела → заявки; оператор КЦ обзванивает по плановым заявкам
 * (E27) → заявки; руководители мониторят → отчёты (ролевой дашборд — Phase 3).
 * Fallback для ролей без рабочего места — линейные с местом уходят в него
 * (homeRouteName, одинаково десктоп/мобилка).
 */
const HOME_ROUTE_BY_ROLE: Record<Role, string> = {
  reception_admin: 'counterparties',
  sales_manager: 'requests',
  appraisal_manager: 'requests',
  kso_specialist: 'requests',
  call_center_operator: 'requests',
  department_head: 'reports',
  branch_director: 'reports',
  call_center_head: 'reports',
  division_head: 'reports',
  regional_kso_head: 'reports',
  commercial_director: 'reports',
  founder: 'reports',
}

// Иконки навигации рабочих мест (E19). Ключ = routeName из реестра WORKSPACES.
// Навигация монохромная (refined): нейтральная иконка, активный пункт = терракота
// (frontend-nav-icons) — per-item nav-токенов больше нет.
const WORKSPACE_NAV_ICON: Record<string, Component> = {
  'workspace-reception': ConciergeBell,
  'workspace-cc': Headset,
  'workspace-mop': Target,
  'workspace-moo': Car,
}

/** Рабочее место под роль смотрящего (по старшей позиции); null — места нет. */
function workspaceForViewer(capabilities: ViewerCapabilities): WorkspaceDef | null {
  const role = primaryRole(capabilities)
  return role === null ? null : (WORKSPACE_BY_ROLE[role] ?? null)
}

/**
 * Имя роута «домашней» страницы под роль (по старшей актуальной позиции). Точка
 * входа `/` редиректит сюда (router), и этот же раздел рендерится домашним пунктом
 * навигации (homeNavItem). На десктопе (`desktop=true`) линейная роль с рабочим
 * местом уходит в него; на мобилке (и роли без места) — прежний поэкранный раздел.
 * Нет актуальных позиций / аноним → «Заявки» (fallback).
 */
export function homeRouteName(capabilities: ViewerCapabilities): string {
  // Админ (суперадмин / org-admin) — дом на дашборде (управленческая раскладка).
  if (isAdminUser(capabilities)) return 'dashboard'
  // Дом роли — её рабочее место (если есть), ОДИНАКОВО на десктопе и мобилке
  // (адаптив везде, [[frontend-page-layout]]). Роли без места — поэкранный раздел.
  const workspace = workspaceForViewer(capabilities)
  if (workspace !== null) return workspace.routeName
  const role = primaryRole(capabilities)
  return role === null ? 'requests' : HOME_ROUTE_BY_ROLE[role]
}

/**
 * Синтетический навигационный пункт рабочего места (в NAV_ITEMS его нет — место
 * не раздел CRM, а домашний экран роли). Иконка/токены чипа — по routeName.
 */
function workspaceNavItem(workspace: WorkspaceDef): NavItem {
  return {
    name: workspace.routeName,
    to: workspace.path,
    icon: WORKSPACE_NAV_ICON[workspace.routeName] ?? CalendarCheck,
    labelKey: workspace.titleKey,
  }
}

/** Домашний пункт навигации (центр таб-бара / первый в сайдбаре) под роль. */
export function homeNavItem(capabilities: ViewerCapabilities): NavItem {
  const workspace = workspaceForViewer(capabilities)
  if (workspace !== null) return workspaceNavItem(workspace)
  const target = homeRouteName(capabilities)
  return NAV_ITEMS.find((item) => item.name === target) ?? NAV_ITEMS[0]
}

/** Пункты меню, видимые пользователю: интерфейс адаптируется под роль (ТЗ §3.3 п.3). */
export function visibleNavItems(capabilities: ViewerCapabilities): NavItem[] {
  return NAV_ITEMS.filter((item) => isItemVisible(item, capabilities))
}

/**
 * Группы сайдбара с отфильтрованными по роли пунктами; домашний раздел исключён
 * (он рендерится отдельным пунктом первым), пустые группы выкидываются —
 * сервисный блок исчезает целиком, если недоступны и «Пользователи», и «Журнал».
 */
export function visibleNavGroups(capabilities: ViewerCapabilities): NavGroup[] {
  const homeName = homeRouteName(capabilities)
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.name !== homeName && isItemVisible(item, capabilities)),
  })).filter((group) => group.items.length > 0)
}

// «Профиль» — раздел без пункта сайдбара (nav: false в реестре), последним табом у рядовых.
const profileItem: NavItem = navItemFor(SECTION_BY_NAME.account, {})

/**
 * Пункты нижнего таб-бара (mobile): видимые по роли разделы в порядке NAV_ITEMS,
 * домашний раздел роли — обычный таб (особым центром больше не выделяется, его
 * активность подсвечивает едущий индикатор). Скрытые флагом `tabBar: false` не
 * входят, кроме случая когда раздел — домашний для роли («Отчёты» у руководителей).
 * «Профиль» — последним табом ТОЛЬКО рядовым (level 0–1): у руководителей таб-бар
 * и так полон, Профиль живёт в аватар-меню шапки. `desktop` всегда false (mobile).
 */
export function visibleTabBarItems(capabilities: ViewerCapabilities): NavItem[] {
  const level = viewerLevel(capabilities)
  // Админ (суперадмин / org-admin): управленческая раскладка — фикс-порядок
  // админ-разделов, доменные разделы уходят в «Ещё» (overflowNavItems). Без
  // рабочего места, домашнего пункта роли и «Профиля» (он в аватар-меню шапки).
  if (isAdminUser(capabilities)) {
    return ADMIN_TAB_BAR_ORDER.map((name) => NAV_ITEMS.find((item) => item.name === name)).filter(
      (item): item is NavItem => item !== undefined && isItemVisible(item, capabilities),
    )
  }
  const homeName = homeRouteName(capabilities)
  // Рабочее место роли (E19) — синтетический пункт (не в NAV_ITEMS): первым табом,
  // как домашний экран роли и на мобилке (адаптив везде).
  const workspace = workspaceForViewer(capabilities)
  const sections = visibleNavItems(capabilities).filter(
    (item) => item.tabBar !== false || item.name === homeName,
  )
  const items = workspace !== null ? [workspaceNavItem(workspace), ...sections] : sections
  return level >= 2 ? items : [...items, profileItem]
}

/**
 * Разделы вне таб-бара (mobile): видимы по роли, но помечены `tabBar: false` и не
 * являются домашними — раскрываются в поповере «Ещё» (5-й таб, §6-BIS.1). Без
 * сайдбара на мобилке это единственный путь к ним. Порядок — как в NAV_ITEMS.
 * Пусто → таб-бар без «Ещё» (у рядовых overflow-разделов нет).
 */
export function overflowNavItems(capabilities: ViewerCapabilities): NavItem[] {
  const inTabBar = new Set(visibleTabBarItems(capabilities).map((item) => item.name))
  return visibleNavItems(capabilities).filter((item) => !inTabBar.has(item.name))
}

/**
 * Активность пункта меню по префиксу пути — подстраницы подсвечивают свой
 * раздел (/counterparties/5 → «Контрагенты», /admin/users/3 → «Пользователи»).
 * Сравнение по route.name не годится: у подстраниц свои имена.
 */
export function isNavItemActive(item: NavItem, path: string): boolean {
  if (item.to === '/') return path === '/'
  return path === item.to || path.startsWith(`${item.to}/`)
}
